import React, { useEffect, useState } from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { Speed, Analytics, Refresh, Warning } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import base_url from "../../constants";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const filterOptions = [
  { label: "1 Day", value: "1d" },
  { label: "2 Day", value: "2d" },
  { label: "3 Day", value: "3d" },
  { label: "4 Day", value: "4d" },
  { label: "5 Day", value: "5d" },
  { label: "6 Day", value: "6d" },
  { label: "7 Day", value: "7d" },
];

const filterChipColors = ["primary", "secondary", "success", "info", "warning", "error", "default"];

const WhatsappBusinessMessageSpeedChart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [messages, setMessages] = useState([]);
  const [labels, setLabels] = useState([]);
  const [filter, setFilter] = useState("1d");
  const [totalMsg, setTotalMsg] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const primaryColor = theme.palette.primary.main;

  const fetchMessageSpeed = async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = Cookies.get("userId");
      const res = await axiosInstance.get(
        `${base_url}/whatsapp-business/message-speed/user?user_id=${userId}&filter=${filter}`
      );

      const serverData = res.data?.data || [];
      setTotalMsg(res.data?.total_messages || 0);

      // --------------------------
      // 1. Determine timeline length
      // --------------------------
      const days = parseInt(filter.replace("d", ""));
      const totalSlots = days * 48; // 48 slots per day (30 min each)

      // --------------------------
      // 2. Build rolling timeline *backwards*
      // --------------------------
      const now = new Date();
      const timelineLabels = [];
      const timelineKeys = []; // for matching

      for (let i = totalSlots - 1; i >= 0; i--) {
        const slotStart = new Date(now.getTime() - i * 30 * 60 * 1000);
        const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);

        const label =
          slotStart.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          }) +
          "\n" +
          slotStart.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }) +
          "-" +
          slotEnd.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });

        timelineLabels.push(label);

        timelineKeys.push({
          start: slotStart,
          end: slotEnd,
        });
      }

      // --------------------------
      // 3. Prepare data array (all zeros)
      // --------------------------
      const combinedMessages = new Array(totalSlots).fill(0);

      // --------------------------
      // 4. Insert server data into correct time slots
      // --------------------------
      serverData.forEach((item) => {
        const eventDate = new Date(
          item.date + " " + item.hour.toString().padStart(2, "0") + ":" +
          (item.interval_block === 0 ? "00" : "30")
        );

        // Find correct slot index
        for (let i = 0; i < totalSlots; i++) {
          if (eventDate >= timelineKeys[i].start && eventDate < timelineKeys[i].end) {
            combinedMessages[i] += item.total_messages;
            break;
          }
        }
      });

      // Save state
      setLabels(timelineLabels);
      setMessages({ SENT: combinedMessages });

    } catch (err) {
      console.error("Error fetching message speed:", err);
      setError(err.response?.data?.message || "Failed to fetch message speed data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessageSpeed();
  }, [filter]);

  return (
    <Box sx={{ ...WB_SX.page, p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={WB_SX.pageTitle}>
          WhatsApp Business Message Speed
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track message delivery speed across 30-minute intervals.
        </Typography>
      </Box>

      <Card sx={{ mb: WB_UI.spacing.sectionGap, boxShadow: "none", ...WB_SX.card }}>
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Speed sx={{ fontSize: 24, color: primaryColor }} />
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Message Speed Analytics
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {filterOptions.map((option, index) => {
                const isActive = filter === option.value;
                const chipColor = filterChipColors[index % filterChipColors.length];
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    color={chipColor}
                    variant={isActive ? "filled" : "outlined"}
                    onClick={() => setFilter(option.value)}
                    clickable
                    sx={{
                      fontWeight: isActive ? 700 : 600,
                      borderRadius: WB_UI.radius.chip,
                    }}
                  />
                );
              })}

              <Chip
                icon={<Refresh />}
                label="Refresh"
                onClick={fetchMessageSpeed}
                color="primary"
                variant="outlined"
                sx={{ cursor: "pointer" }}
              />
            </Box>
          </Box>

          {/* Stats */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <Card
              sx={{
                flex: 1,
                minWidth: 200,
                boxShadow: "none",
                ...WB_SX.card,
                backgroundColor: WB_UI.color.cardBgStrong,
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Analytics sx={{ color: primaryColor }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", fontWeight: 700 }}
                  >
                    Total Messages
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{ mt: 1, fontWeight: 800, color: "text.primary" }}
                >
                  {totalMsg}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Error Message */}
          {error && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                mb: 2,
                bgcolor: "error.light",
                color: "error.dark",
                borderRadius: 1,
              }}
            >
              <Warning />
              <Typography>{error}</Typography>
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Chart */}
          {!loading && !error && (
            <Box
              sx={{
                height: 400,
                borderRadius: WB_UI.radius.card,
                border: `1px solid ${WB_UI.color.border}`,
                backgroundColor: WB_UI.color.cardBgStrong,
                overflow: "hidden",
              }}
            >
              {labels.length > 0 ? (
                <BarChart
                  xAxis={[
                    {
                      id: "timeSlots",
                      data: labels,
                      scaleType: "band",
                      tickLabelStyle: { whiteSpace: "pre" },
                      labelStyle: {
                        fontSize: 12,
                        fontWeight: "bold",
                        fill: theme.palette.text.primary
                      }
                    },
                  ]}
                  yAxis={[
                    {
                      tickMinStep: 1,  
                      min: 0, 
                      label: "Number of Messages",
                      minPadding: 5,  
                      maxPadding: 10,
                      labelStyle: {
                        fontSize: 12,
                        fontWeight: "bold",
                        fill: theme.palette.text.primary
                      },
                      max:
                        Object.keys(messages).length > 0
                          ? Math.max(
                              ...Object.values(messages)
                                .flat()
                            ) + 2
                          : 10,
                    }
                  ]}
                  series={Object.keys(messages).map((type, index) => ({
                    data: messages.SENT,
                    label: "SENT",
                    stack: "msg",
                    color: primaryColor
                  }))}
                  height={400}
                  margin={{ left: 70, right: 30, top: 30, bottom: 70 }}
                  grid={{ vertical: true, horizontal: true }}
                  borderRadius={8}
                  sx={{
                    '& .MuiChartsAxis-tickLabel': {
                      fontSize: isMobile ? 8 : 10,
                    }
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    color: "text.secondary",
                  }}
                >
                  <Typography>No message data available for the selected period</Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Box
        sx={{
          p: 2,
          ...WB_SX.card,
          boxShadow: "none",
          backgroundColor: WB_UI.color.cardBgStrong,
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">
          <strong>Note:</strong> This chart shows the speed at which WhatsApp Business messages
          were sent over the selected time period. Data is grouped into 30-minute intervals.
          Only messages with status SENT, DELIVERED, or READ are included.
        </Typography>
      </Box>
    </Box>
  );
};

export default WhatsappBusinessMessageSpeedChart;

